import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiX } from 'react-icons/bi';
import {
  Slide,
  ToastContainer,
  cssTransition,
  type CloseButtonProps,
  type ToastPosition,
} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AppToastContainer.css';

const MOBILE_QUERY = '(max-width: 767px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Opacity-only fallback transition for `prefers-reduced-motion: reduce` — mirrors the app's
 * existing rule of zeroing out `--motion-*` (tokens.css) for the rest of the UI; react-toastify's
 * animations live in their own CSS namespace so they need an explicit swap rather than a shared
 * custom property. Duration is intentionally near-zero, not a slowed-down slide. */
const NoMotionTransition = cssTransition({
  enter: 'fa-toast-fade-enter',
  exit: 'fa-toast-fade-exit',
  collapseDuration: 0,
});

function usePrefersReducedMotion(): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(REDUCED_MOTION_QUERY).matches,
  );
  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return matches;
}

function useIsMobileViewport(): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  );
  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return matches;
}

/**
 * App-wide toast container — docs/design/12-reskin-spec.md §7.1/§7.2. Mounted once in
 * `AppProviders`, RTL-aware (position computed from `i18n.dir()`, not hardcoded), collapses to
 * `bottom-center` below `bp.md` (768px). Actual toast calls (success/error per action) are wired
 * by `T95` via `src/lib/toast.ts` — this component only owns placement/transition/chrome.
 */
export function AppToastContainer() {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobileViewport();
  const prefersReducedMotion = usePrefersReducedMotion();
  const dir = i18n.dir();
  const isRtl = dir === 'rtl';

  const position: ToastPosition = isMobile ? 'bottom-center' : isRtl ? 'top-left' : 'top-right';

  function renderCloseButton({ closeToast, theme }: CloseButtonProps) {
    return (
      <button
        type="button"
        className={`Toastify__close-button Toastify__close-button--${theme}`}
        aria-label={t('toast.dismiss')}
        onClick={(e) => {
          e.stopPropagation();
          closeToast(true);
        }}
      >
        <BiX aria-hidden="true" />
      </button>
    );
  }

  return (
    <ToastContainer
      position={position}
      rtl={isRtl}
      transition={prefersReducedMotion ? NoMotionTransition : Slide}
      autoClose={4000}
      pauseOnHover
      pauseOnFocusLoss
      newestOnTop
      closeButton={renderCloseButton}
    />
  );
}
